import type { FC } from "react";

import { noop } from "lodash";

import { SWReactIcons } from "@similarweb/icons";
import { Type } from "@similarweb/ui-components";

import { adsTargetURL } from "app/filters/ngFilters";
import { StyledItemIcon } from "pro-features/components/core cells/src/CoreRecentCell/StyledComponents";

import type { ITableCellProps } from "../interfaces/ITableCellProps";
import { trackEvent } from "../SWReactTableTrackEvent";

import {
    AdUnitComponent,
    AdPreview,
    AdPreviewPrefix,
    H3Container,
    ItemIconContainer,
    AdTargetUrl,
    AdDescription,
    TextAdWrapper,
    ShoppingAdContainer,
    ImageContainer,
    ShoppingAdWrapper,
} from "./AdUnitStyles";
import { UpgradeLink } from "./UpgradeLink";

// Re-export for backward compatibility
export {
    AdUnitComponent,
    AdPreview,
    H3Container,
    AdTargetUrl,
    AdDescription,
    TextAdWrapper,
    ImageContainer,
    ShoppingAdWrapper,
};

export const TextAd = ({
    targetUrl,
    Description,
    DestUrl = undefined,
    Title,
    onClick,
    withBorder = true,
    withPadding = true,
    DestUrlPrefix = undefined,
    DestUrlFavicon = undefined,
    DestUrlFaviconIconType = Type.Website,
}: FixmeType) => {
    return (
        <AdUnitComponent
            withBorder={withBorder}
            withPadding={withPadding}
        >
            {DestUrl ? (
                <AdPreview>
                    {DestUrlFavicon ? (
                        <ItemIconContainer>
                            <StyledItemIcon
                                iconType={DestUrlFaviconIconType}
                                iconSrc={DestUrlFavicon}
                            />
                        </ItemIconContainer>
                    ) : null}
                    {DestUrlPrefix ? <AdPreviewPrefix>{DestUrlPrefix}</AdPreviewPrefix> : null}
                    <span>{DestUrl}</span>
                </AdPreview>
            ) : null}
            <H3Container>
                {targetUrl ? (
                    <AdTargetUrl
                        className="ad-target-url"
                        href={targetUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={onClick}
                    >
                        {Title}
                    </AdTargetUrl>
                ) : (
                    <span className="ad-target-url">{Title}</span>
                )}
            </H3Container>
            <AdDescription
                className="description"
                style={{ lineHeight: "1.33" }}
            >
                {Description}
            </AdDescription>
        </AdUnitComponent>
    );
};

function decodeHexedImage(url: string) {
    return url.replace(/\\x([0-9A-Fa-f]{2})/g, (all: string, hexNumber: string) =>
        String.fromCharCode(parseInt(hexNumber, 16)),
    );
}

export const ShoppingAd = ({
    Title,
    Price,
    Brand,
    ImageUrl,
    onImageClick = noop,
    onNoImageClick = noop,
}: FixmeType) => {
    const isImageClickable = !!onImageClick || (!ImageUrl && !!onNoImageClick);
    return (
        <ShoppingAdContainer isImgClickable={isImageClickable}>
            <ImageContainer
                className="image-container"
                isImgClickable={isImageClickable}
            >
                {ImageUrl ? (
                    <img
                        src={decodeHexedImage(ImageUrl)}
                        onClick={onImageClick}
                    />
                ) : (
                    <div onClick={onNoImageClick}>
                        {/* @ts-expect-error icon with this name doesn't exist */}
                        <SWReactIcons iconName="no-image-available" />
                    </div>
                )}
            </ImageContainer>
            <div className="details">
                <span className="price">{Price}</span>
                <span className="title">{Title}</span>
                <span className="brand">{Brand}</span>
            </div>
        </ShoppingAdContainer>
    );
};

export const AdUnit: FC<ITableCellProps> = ({ value, tableOptions }) => {
    const targetUrl = adsTargetURL()(value);
    const { Title, Type, Description, DestUrl } = value;
    const onClick = () => trackEvent(tableOptions, "External Link", targetUrl, "click");
    switch (Title) {
        case "grid.upgrade":
            return <UpgradeLink hookType="pages" />;
        default:
            switch (Type) {
                case "Text":
                    return (
                        <TextAd
                            {...{ Title, Description, DestUrl, targetUrl, onClick }}
                            withBorder={false}
                            withPadding={false}
                        />
                    );
                case "Shopping":
                    const { Price, Brand, ImageUrl } = value;
                    return (
                        <ShoppingAd
                            {...{
                                Title,
                                Price,
                                Brand,
                                ImageUrl,
                                Description,
                                DestUrl,
                                targetUrl,
                                onClick,
                            }}
                        />
                    );
            }
    }
};
AdUnit.displayName = "AdUnit";
